/* eslint-disable react/no-unknown-property */
import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment, Text } from "@react-three/drei";
import * as THREE from "three";

const PHASES = ["Interphase", "Prophase", "Metaphase", "Anaphase", "Telophase"];

const Cell = ({ phase, cycle }) => {
  // cycle is 0..1 within the current phase
  const nucleusRef = useRef();
  const chrom1 = useRef();
  const chrom2 = useRef();

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (nucleusRef.current) nucleusRef.current.rotation.y = t * 0.15;
  });

  // Positions of the two chromatids based on phase
  let sep = 0;
  let condense = 0.4;
  switch (phase) {
    case 0: // Interphase — diffuse nucleus, no visible chromosomes
      sep = 0; condense = 0.15; break;
    case 1: // Prophase — chromosomes condense
      sep = 0; condense = 0.4 + cycle * 0.2; break;
    case 2: // Metaphase — line up on equator
      sep = 0; condense = 0.7; break;
    case 3: // Anaphase — pull apart
      sep = 0.4 + cycle * 1.2; condense = 0.7; break;
    case 4: // Telophase — new nuclei form
      sep = 1.6; condense = 0.4 - cycle * 0.2; break;
    default: break;
  }

  return (
    <group>
      {/* Cell membrane */}
      <mesh>
        <sphereGeometry args={[2.4, 32, 32]} />
        <meshStandardMaterial color="#22d3ee" transparent opacity={0.08} emissive="#0891b2" emissiveIntensity={0.15} />
      </mesh>

      {/* Nuclear envelope — dissolves in prophase */}
      {phase <= 1 && (
        <mesh ref={nucleusRef}>
          <sphereGeometry args={[1.4, 32, 32]} />
          <meshStandardMaterial color="#a855f7" transparent opacity={0.25 - phase * 0.15} emissive="#7c3aed" emissiveIntensity={0.2} />
        </mesh>
      )}
      {/* Two new nuclei in telophase */}
      {phase === 4 && (
        <>
          <mesh position={[-sep, 0, 0]}>
            <sphereGeometry args={[1.1, 24, 24]} />
            <meshStandardMaterial color="#a855f7" transparent opacity={0.25 + cycle * 0.15} emissive="#7c3aed" emissiveIntensity={0.2} />
          </mesh>
          <mesh position={[sep, 0, 0]}>
            <sphereGeometry args={[1.1, 24, 24]} />
            <meshStandardMaterial color="#a855f7" transparent opacity={0.25 + cycle * 0.15} emissive="#7c3aed" emissiveIntensity={0.2} />
          </mesh>
        </>
      )}

      {/* Chromosomes — pair 1 */}
      <mesh ref={chrom1} position={[-sep, 0.4, 0]}>
        <capsuleGeometry args={[condense * 0.18, condense * 0.9, 8, 16]} />
        <meshStandardMaterial color="#f43f5e" emissive="#e11d48" emissiveIntensity={0.45} />
      </mesh>
      <mesh ref={chrom2} position={[sep, 0.4, 0]}>
        <capsuleGeometry args={[condense * 0.18, condense * 0.9, 8, 16]} />
        <meshStandardMaterial color="#f43f5e" emissive="#e11d48" emissiveIntensity={0.45} />
      </mesh>

      {/* Chromosomes — pair 2 */}
      <mesh position={[-sep, -0.4, 0.4]}>
        <capsuleGeometry args={[condense * 0.15, condense * 0.7, 8, 16]} />
        <meshStandardMaterial color="#22d3ee" emissive="#0891b2" emissiveIntensity={0.45} />
      </mesh>
      <mesh position={[sep, -0.4, 0.4]}>
        <capsuleGeometry args={[condense * 0.15, condense * 0.7, 8, 16]} />
        <meshStandardMaterial color="#22d3ee" emissive="#0891b2" emissiveIntensity={0.45} />
      </mesh>

      {/* Spindle fibers in anaphase */}
      {phase === 3 && (
        <>
          <mesh position={[-2.2, 0, 0]}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color="#fde047" emissive="#facc15" emissiveIntensity={0.6} />
          </mesh>
          <mesh position={[2.2, 0, 0]}>
            <sphereGeometry args={[0.12, 12, 12]} />
            <meshStandardMaterial color="#fde047" emissive="#facc15" emissiveIntensity={0.6} />
          </mesh>
        </>
      )}
    </group>
  );
};

const Mitosis3D = ({ params }) => {
  const phaseIdx = Math.max(0, Math.min(4, Math.floor(+params.phase ?? 2)));
  const cycle = (+params.progress ?? 0.5);

  return (
    <Canvas camera={{ position: [0, 1, 7], fov: 45 }} dpr={[1, 2]}>
      <color attach="background" args={["#020617"]} />
      <ambientLight intensity={0.4} />
      <pointLight position={[4, 4, 5]} intensity={1.2} color="#22d3ee" />
      <pointLight position={[-4, -2, 3]} intensity={0.6} color="#a855f7" />

      <Cell phase={phaseIdx} cycle={cycle} />

      <Text position={[0, 3.4, 0]} fontSize={0.35} color="#22d3ee" anchorX="center">
        {PHASES[phaseIdx]}
      </Text>
      <Text position={[0, -3.4, 0]} fontSize={0.18} color="#94a3b8" anchorX="center">
        Mitosis · Somatic cell division
      </Text>

      <Environment preset="night" />
      <OrbitControls enablePan={false} minDistance={4} maxDistance={12} />
    </Canvas>
  );
};

export default Mitosis3D;
